/**
 * The best puns of the last week, for the signed-out screens.
 *
 * Signed out, so it cannot go through `usePunFeed`: that hook exists to serve a
 * viewer — it resolves author names, tracks a crown budget and writes
 * reactions, none of which mean anything to someone who does not have an
 * account yet. This reads puns and stops there.
 *
 * `puns` is world-readable in `firestore.rules` precisely so this can work
 * before sign-in. App Check still applies, so a broken App Check config makes
 * the panel quietly empty rather than erroring — see CLAUDE.md gotcha 13.
 */
import { useEffect, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';

import { firestore } from '../firebase';
import { pacificDayString, shiftPacificDay } from '../../shared/dates';
import type { Pun } from '../../shared/schemas';
import { punsFromSnapshot, sortPunsByCrowns } from './puns';

/** How far back to look, counting today. */
const WINDOW_DAYS = 7;

/**
 * Most a single page view will read.
 *
 * Crowns live in a map, so "most crowned" cannot be an `orderBy` and the
 * ranking has to happen on the client — which means the query decides how much
 * it is ranking. Unbounded, one visit to a page nobody has logged into yet
 * costs a document read per pun posted that week, and that number only grows.
 * Ordering by day and capping keeps the cost flat; past the cap it ranks the
 * most recent 120 rather than the whole week, which is a distinction no visitor
 * can perceive.
 */
const MAX_DOCS = 120;

export function useTopPuns(count: number): Pun[] {
  const [puns, setPuns] = useState<Pun[]>([]);

  useEffect(() => {
    let cancelled = false;

    /*
     * `pstDay` rather than `createdAt`, matching every other day filter in the
     * app. The old version compared `createdAt` against a Date built from the
     * viewer's clock, so "the last week" moved with the reader's timezone
     * instead of with the game's day boundary.
     *
     * Yesterday, not today: a settled day has its crowns in, and today's puns
     * are the ones the feed gate deliberately withholds from people who have
     * not posted. Showing them on the signed-out page would hand them to
     * someone who has not even signed up.
     */
    const today = pacificDayString();

    getDocs(
      query(
        collection(firestore, 'puns'),
        where('pstDay', '>=', shiftPacificDay(today, -(WINDOW_DAYS - 1))),
        where('pstDay', '<=', shiftPacificDay(today, -1)),
        orderBy('pstDay', 'desc'),
        limit(MAX_DOCS),
      ),
    )
      .then((snapshot) => {
        if (cancelled) return;
        setPuns(sortPunsByCrowns(punsFromSnapshot(snapshot, 'useTopPuns')).slice(0, count));
      })
      .catch((err) => console.error('[useTopPuns] could not load top puns:', err));

    return () => {
      cancelled = true;
    };
  }, [count]);

  return puns;
}
